import { useState, useMemo } from 'react';
import useSWR from 'swr';
import { SalesOrderDetailsDialog } from '../components/sales-orders/SalesOrderDetailsDialog';
import {
    Plus,
    Search,
    Package,
    Truck,
    CheckCircle2,
    CircleDashed,
    PackageCheck,
    X,
    RotateCw,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    CalendarCheck,
    AlertTriangle,
    Clock,
    ArchiveX,
    Activity,
    Lock,
    Mail,
} from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface SalesOrder {
    id: string;
    orderNumber: string;
    customerName: string;
    status: string;
    itemsCount?: number;
    shipDate?: string;
    notes?: string;
    createdAt?: string;
}

const STATUS_CONFIG: Record<string, { label: string; icon: any; bg: string; color: string; border: string }> = {
    open: { label: 'Open', icon: CircleDashed, bg: 'bg-slate-50', color: 'text-slate-600', border: 'border-slate-200' },
    picking: { label: 'Picking', icon: Activity, bg: 'bg-blue-50', color: 'text-blue-700', border: 'border-blue-200' },
    packed: { label: 'Packed', icon: Package, bg: 'bg-indigo-50', color: 'text-indigo-700', border: 'border-indigo-200' },
    ready_to_ship: { label: 'Ready', icon: PackageCheck, bg: 'bg-purple-50', color: 'text-purple-700', border: 'border-purple-200' },
    shipped: { label: 'Shipped', icon: Truck, bg: 'bg-emerald-50', color: 'text-emerald-700', border: 'border-emerald-200' },
    closed: { label: 'Closed', icon: CheckCircle2, bg: 'bg-slate-50', color: 'text-slate-500', border: 'border-slate-200' },
};

type SortField = 'orderNumber' | 'customerName' | 'status' | 'itemsCount' | 'shipDate' | 'createdAt';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'active' | 'all' | 'open' | 'picking' | 'packed' | 'shipped' | 'closed';
type AttentionFilter = 'ready_to_ship' | 'ships_today' | 'overdue' | null;

const ACTIVE_STATUSES = new Set(['open', 'picking', 'packed']);

// Date helpers
function isToday(dateStr: string): boolean {
    const d = new Date(dateStr);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function isPastDate(dateStr: string): boolean {
    const d = new Date(dateStr);
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return d < todayStart;
}

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const fetcher = async (url: string, token: string) => {
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
    return res.json();
};

export function SalesOrders() {
    if (typeof window !== 'undefined' && window.location.search.includes('debug')) {
        console.log('[SalesOrders] component rendering, token:', !!useAuthStore.getState().token);
    }
    const { token, isAuthenticated, hasHydrated, user } = useAuthStore();
    const isOperationsEntitled = user?.effectiveTier === 'operations';
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [sortField, setSortField] = useState<SortField>('createdAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>(null);

    // Debug banner: visible only with ?debug=1
    const showDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');

    // Form state
    const [newOrder, setNewOrder] = useState({ orderNumber: '', customerName: '', itemsCount: '', shipDate: '', notes: '' });

    const { data, error, isLoading, isValidating, mutate } = useSWR(
        token ? ['/api/sales-orders', token] : null,
        ([url, tkn]) => fetcher(url, tkn),
        { revalidateOnFocus: true }
    );

    // When token hasn't arrived yet, SWR returns { data: undefined, isLoading: false }
    // We treat this as still loading so the UI shows a spinner, not "empty"
    const waitingForToken = !token && isAuthenticated;

    const orders: SalesOrder[] = data?.orders || [];

    // Status counts (computed from ALL orders before filtering)
    const statusCounts = useMemo(() => {
        const counts: Record<string, number> = { all: orders.length, active: 0 };
        orders.forEach(o => {
            counts[o.status] = (counts[o.status] || 0) + 1;
            if (ACTIVE_STATUSES.has(o.status)) counts.active++;
        });
        return counts;
    }, [orders]);

    // Attention counts (computed from ALL orders, independent of filters)
    const attentionCounts = useMemo(() => {
        const counts = { ready_to_ship: 0, ships_today: 0, overdue: 0 };
        orders.forEach(o => {
            if (o.status === 'packed') counts.ready_to_ship++;
            if (o.shipDate && isToday(o.shipDate) && o.status !== 'shipped' && o.status !== 'closed') counts.ships_today++;
            if (o.shipDate && isPastDate(o.shipDate) && o.status !== 'shipped' && o.status !== 'closed') counts.overdue++;
        });
        return counts;
    }, [orders]);

    // Attention filter predicate
    const matchesAttention = (o: SalesOrder): boolean => {
        if (!attentionFilter) return true;
        switch (attentionFilter) {
            case 'ready_to_ship': return o.status === 'packed';
            case 'ships_today': return !!(o.shipDate && isToday(o.shipDate) && o.status !== 'shipped' && o.status !== 'closed');
            case 'overdue': return !!(o.shipDate && isPastDate(o.shipDate) && o.status !== 'shipped' && o.status !== 'closed');
            default: return true;
        }
    };

    // Filter & Sort
    const filtered = useMemo(() => {
        let result = [...orders];

        // 1. Attention filter (overrides status filter)
        if (attentionFilter) {
            result = result.filter(matchesAttention);
        }

        // 2. Status filter (skipped when attention filter is active)
        if (!attentionFilter) {
            if (statusFilter === 'active') {
                result = result.filter(o => ACTIVE_STATUSES.has(o.status));
            } else if (statusFilter !== 'all') {
                result = result.filter(o => o.status === statusFilter);
            }
        }

        // 3. Search
        if (search) {
            const q = search.toLowerCase();
            result = result.filter(o =>
                o.orderNumber.toLowerCase().includes(q) ||
                o.customerName.toLowerCase().includes(q)
            );
        }

        return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders, search, statusFilter, attentionFilter]);

    const sorted = useMemo(() => {
        const list = [...filtered];
        list.sort((a, b) => {
            let aVal: any;
            let bVal: any;
            switch (sortField) {
                case 'orderNumber':
                    aVal = a.orderNumber.toLowerCase();
                    bVal = b.orderNumber.toLowerCase();
                    break;
                case 'customerName':
                    aVal = a.customerName.toLowerCase();
                    bVal = b.customerName.toLowerCase();
                    break;
                case 'status':
                    aVal = a.status;
                    bVal = b.status;
                    break;
                case 'itemsCount':
                    aVal = a.itemsCount ?? 0;
                    bVal = b.itemsCount ?? 0;
                    break;
                case 'shipDate':
                    aVal = a.shipDate ?? '';
                    bVal = b.shipDate ?? '';
                    break;
                case 'createdAt':
                    aVal = a.createdAt ?? '';
                    bVal = b.createdAt ?? '';
                    break;
                default:
                    return 0;
            }
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return list;
    }, [filtered, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const SortIcon = ({ field }: { field: SortField }) => {
        if (sortField !== field) return <ChevronsUpDown className="w-3 h-3 text-slate-300" />;
        return sortDirection === 'asc'
            ? <ChevronUp className="w-3 h-3 text-slate-700" />
            : <ChevronDown className="w-3 h-3 text-slate-700" />;
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newOrder.orderNumber.trim() || !newOrder.customerName.trim()) return;
        setCreating(true);
        try {
            const result: any = await api.request('/sales-orders', {
                method: 'POST',
                body: JSON.stringify({
                    orderNumber: newOrder.orderNumber,
                    customerName: newOrder.customerName,
                    itemsCount: newOrder.itemsCount ? Number(newOrder.itemsCount) : 0,
                    shipDate: newOrder.shipDate || undefined,
                    notes: newOrder.notes || undefined,
                }),
            });
            toast.success('Sales order created');
            setNewOrder({ orderNumber: '', customerName: '', itemsCount: '', shipDate: '', notes: '' });
            setShowCreate(false);

            // Optimistic: prepend new order to list cache
            mutate((current: any) => {
                if (!current?.orders) return current;
                return { ...current, orders: [result.order, ...current.orders] };
            }, { revalidate: true });

            // Auto-open the details dialog for the new order
            if (result.order?.id) {
                setSelectedOrderId(result.order.id);
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to create order');
        } finally {
            setCreating(false);
        }
    };

    const handleStatusChange = async (id: string, newStatus: string) => {
        try {
            await api.request(`/sales-orders/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status: newStatus }),
            });
            toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
            mutate();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update status');
        }
    };

    const handleAttentionClick = (key: AttentionFilter) => {
        if (attentionFilter === key) {
            setAttentionFilter(null);
        } else {
            setAttentionFilter(key);
            setStatusFilter('all'); // reset status pill to avoid hidden conflicts
        }
    };

    const handleStatusPillClick = (key: StatusFilter) => {
        setStatusFilter(key);
        setAttentionFilter(null); // clear attention filter when using status pills
    };

    const handleReset = () => {
        setSearch('');
        setStatusFilter('active');
        setSortField('createdAt');
        setSortDirection('desc');
        setAttentionFilter(null);
    };

    const hasActiveFilters = search || statusFilter !== 'active' || sortField !== 'createdAt' || sortDirection !== 'desc' || attentionFilter !== null;

    // Log errors so they're never swallowed
    if (error) {
        console.error('[SalesOrders] fetch error:', error.message);
    }

    // Attention card config
    const attentionCards: { key: NonNullable<AttentionFilter>; count: number; icon: React.ReactNode; color: string; activeColor: string; label: string }[] = [
        { key: 'ready_to_ship', count: attentionCounts.ready_to_ship, icon: <PackageCheck size={15} />, color: 'text-purple-500', activeColor: 'ring-purple-400 bg-purple-50', label: 'Ready to Ship' },
        { key: 'ships_today', count: attentionCounts.ships_today, icon: <CalendarCheck size={15} />, color: 'text-cyan-500', activeColor: 'ring-cyan-400 bg-cyan-50', label: 'Ships Today' },
        { key: 'overdue', count: attentionCounts.overdue, icon: <AlertTriangle size={15} />, color: 'text-red-500', activeColor: 'ring-red-400 bg-red-50', label: 'Overdue' },
    ];

    // Active attention label for filter chip
    const activeAttentionLabel = attentionFilter
        ? (() => {
            const card = attentionCards.find(c => c.key === attentionFilter);
            if (!card) return attentionFilter;
            return `${card.count} ${card.label}`;
          })()
        : null;

    // Status pills
    const statusPills: { key: StatusFilter; label: string; count: number }[] = [
        { key: 'all', label: 'All', count: statusCounts.all || 0 },
        { key: 'active', label: 'Active', count: statusCounts.active || 0 },
        { key: 'open', label: 'Open', count: statusCounts.open || 0 },
        { key: 'picking', label: 'Picking', count: statusCounts.picking || 0 },
        { key: 'packed', label: 'Packed', count: statusCounts.packed || 0 },
        { key: 'shipped', label: 'Shipped', count: statusCounts.shipped || 0 },
        { key: 'closed', label: 'Closed', count: statusCounts.closed || 0 },
    ];

    // Row emphasis
    const getRowEmphasis = (o: SalesOrder): string => {
        if (o.status === 'closed') return 'opacity-50';
        if (o.status === 'shipped') return 'opacity-70';
        
        // Only flag if severely overdue (e.g., still 'open' well past ship date)
        if (o.shipDate && isPastDate(o.shipDate) && o.status === 'open') {
            const ship = new Date(o.shipDate).getTime();
            const now = new Date().getTime();
            // If overdue by more than 7 days, it's a true problem in test environments
            if (now - ship > 7 * 24 * 60 * 60 * 1000) {
                return 'border-l-4 border-l-rose-400 bg-rose-50/20';
            }
        }
        
        return '';
    };

    // Loading skeleton (matching PO page pattern)
    if (isLoading || waitingForToken) return (
        <div className="h-full flex flex-col overflow-hidden py-6">
            <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 space-y-4">
                <div className="flex justify-between items-center">
                    <div className="space-y-2">
                        <div className="h-8 w-52 bg-slate-200 rounded animate-pulse" />
                        <div className="h-4 w-72 bg-slate-200/60 rounded animate-pulse" />
                    </div>
                    <div className="h-9 w-32 bg-slate-200 rounded-lg animate-pulse" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-200 px-3 py-2.5">
                        <div className="flex gap-6">
                            {[80, 120, 56, 40, 40, 72, 72, 56].map((w, i) => (
                                <div key={i} className="h-3 rounded animate-pulse bg-slate-200" style={{ width: w }} />
                            ))}
                        </div>
                    </div>
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-6 px-3 py-3 border-b border-slate-100">
                            <div className="h-4 w-20 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-28 bg-slate-100 rounded animate-pulse" />
                            <div className="h-5 w-16 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-8 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-8 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-20 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-16 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-16 bg-slate-100 rounded animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (error) return (
        <div className="h-full flex flex-col items-center justify-center text-red-500 space-y-2">
            <ArchiveX size={32} />
            <p className="font-medium text-slate-900">Failed to load sales orders</p>
            <p className="text-sm text-slate-500">{error.message}</p>
            <button
                onClick={() => mutate()}
                className="mt-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors text-slate-700 font-medium"
            >
                Retry
            </button>
        </div>
    );

    return (
        <div className="h-full flex flex-col overflow-hidden py-6">
          <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 flex flex-col flex-1 overflow-hidden">
            {/* Page Header */}
            <div className="shrink-0 mb-5">
                {/* Title + Create SO */}
                <div className="flex justify-between items-start mb-5">
                    <div className="space-y-1">
                        <h1 className="text-2xl font-bold text-slate-900 font-outfit tracking-tight flex items-center gap-2.5">
                            <Truck className="text-blue-600" size={22} />
                            Sales Orders
                        </h1>
                        <p className="text-sm text-slate-500">Manage outbound orders and shipments.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isOperationsEntitled && (
                            <button
                                onClick={() => setShowCreate(true)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                            >
                                <Plus size={16} />
                                New SO
                            </button>
                        )}
                    </div>
                </div>

                {!isOperationsEntitled && (
                    <div
                        data-testid="sales-orders-operations-upgrade-banner"
                        className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-amber-500 shrink-0" />
                                <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30">
                                    Operations Plan Feature
                                </span>
                                <span className="text-xs font-semibold text-slate-500">
                                    Starter Plan ($99/mo) — Read-Only View
                                </span>
                            </div>
                            <h2 className="text-sm font-bold text-slate-900">
                                Upgrade to Operations ($199 USD/month per facility) for Outbound Sales Order Management
                            </h2>
                            <p className="text-xs text-slate-600 max-w-2xl">
                                Creating outbound sales orders and updating order picking, packing, and shipping statuses require the Operations plan. Existing historical sales orders remain viewable below in read-only mode.
                            </p>
                        </div>
                        <a
                            href="mailto:support@baywhiz.com?subject=Upgrade%20to%20BayWhiz%20Operations%20Plan"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs transition-colors shrink-0"
                        >
                            <Mail className="w-4 h-4" />
                            Contact support@baywhiz.com to Upgrade
                        </a>
                    </div>
                )}

                {/* Debug banner \u2014 ?debug=1 */}
                {showDebug && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs font-mono text-amber-300 space-y-0.5">
                        <div>hasHydrated: <b>{String(hasHydrated)}</b> | isAuthenticated: <b>{String(isAuthenticated)}</b></div>
                        <div>role: <b>{user?.role ?? 'null'}</b> | token: <b>{token ? 'present' : 'MISSING'}</b></div>
                        <div>SWR key: <b>{token ? '[/api/sales-orders, token]' : 'null (paused)'}</b></div>
                    </div>
                )}

                {/* Toolbar Card */}
                <div className="bg-white/70 border border-slate-200 rounded-xl px-3 py-2.5 flex flex-wrap items-center gap-2">
                    {/* Status pills */}
                    <div className="flex items-center gap-1 flex-wrap">
                        {statusPills.map(pill => (
                            <button
                                key={pill.key}
                                onClick={() => handleStatusPillClick(pill.key)}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[12px] font-medium rounded transition-colors ${
                                    statusFilter === pill.key
                                        ? 'bg-slate-700 text-white'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                                {pill.label}
                                <span className={`text-[10px] font-mono px-1 py-px rounded ${
                                    statusFilter === pill.key
                                        ? 'bg-slate-600 text-white'
                                        : 'bg-slate-100 text-slate-400'
                                }`}>
                                    {pill.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Divider */}
                    <div className="w-px h-6 bg-slate-200 hidden sm:block mx-2"></div>

                    {/* Search */}
                    <div className="relative flex-1 min-w-[180px] max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search SO # or customer..."
                            className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-8 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-300 outline-none transition-all"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    {/* Issues / Attention Filter */}
                    <div className="flex items-center gap-1.5">
                        <AlertTriangle size={14} className="text-slate-400" />
                        <select
                            value={attentionFilter || 'all'}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'all') {
                                    setAttentionFilter(null);
                                } else {
                                    setAttentionFilter(val as AttentionFilter);
                                    setStatusFilter('all');
                                }
                            }}
                            className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-slate-700 focus:ring-1 focus:ring-blue-500 outline-none cursor-pointer hover:bg-slate-50"
                        >
                            <option value="all">All Issues</option>
                            {attentionCards.map(card => (
                                <option key={card.key} value={card.key}>
                                    {card.label} ({card.count})
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Refresh & Reset */}
                    <div className="ml-auto flex items-center gap-1">
                        <button
                            onClick={() => mutate()}
                            disabled={isValidating}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-100 disabled:opacity-50"
                            title="Refresh"
                        >
                            <RotateCw size={16} className={isValidating ? 'animate-spin' : ''} />
                        </button>
                        {hasActiveFilters && (
                            <button
                                onClick={handleReset}
                                className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-2 py-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Active attention filter chip */}
            {activeAttentionLabel && (
                <div className="shrink-0 mb-2">
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                        Filtered by: <span className="font-semibold">{activeAttentionLabel}</span>
                        <button
                            onClick={() => setAttentionFilter(null)}
                            className="ml-0.5 text-slate-400 hover:text-slate-700 transition-colors"
                        >
                            <X size={13} />
                        </button>
                    </span>
                </div>
            )}

            {/* Table Area */}
            <div className="flex-1 overflow-y-auto pb-4">
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead>
                            <tr className="bg-slate-50/80 border-b border-slate-200">
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleSort('orderNumber')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        SO # <SortIcon field="orderNumber" />
                                    </span>
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleSort('customerName')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Customer <SortIcon field="customerName" />
                                    </span>
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleSort('status')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Status <SortIcon field="status" />
                                    </span>
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleSort('itemsCount')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Items <SortIcon field="itemsCount" />
                                    </span>
                                </th>
                                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5">
                                    Dock
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleSort('shipDate')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Ship Date <SortIcon field="shipDate" />
                                    </span>
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleSort('createdAt')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Created <SortIcon field="createdAt" />
                                    </span>
                                </th>
                                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center py-12">
                                        <div className="flex flex-col items-center text-slate-400">
                                            <ArchiveX size={40} className="mb-3 text-slate-300" />
                                            <p className="text-sm font-medium text-slate-500">No sales orders found</p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                {hasActiveFilters
                                                    ? 'Try adjusting your filters or search term'
                                                    : 'Create a new SO to get started'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                sorted.map(order => {
                                    const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.open;
                                    const Icon = cfg.icon;
                                    return (
                                        <tr
                                            key={order.id}
                                            onClick={() => setSelectedOrderId(order.id)}
                                            className={`group hover:bg-slate-50/70 border-b border-slate-100 cursor-pointer transition-colors ${getRowEmphasis(order)}`}
                                        >
                                            {/* SO # */}
                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                <span className="font-mono text-[13px] text-slate-900 font-medium">{order.orderNumber}</span>
                                            </td>
                                            {/* Customer */}
                                            <td className="px-3 py-2.5">
                                                <span className="block max-w-[180px] truncate text-[13px] text-slate-700" title={order.customerName}>
                                                    {order.customerName}
                                                </span>
                                            </td>
                                            {/* Status */}
                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                                                    <Icon className="w-3 h-3" />
                                                    {cfg.label}
                                                </span>
                                            </td>
                                            {/* Items */}
                                            <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-700 tabular-nums">
                                                {order.itemsCount != null ? order.itemsCount : '\u2014'}
                                            </td>
                                            {/* Dock */}
                                            <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-500 font-mono">
                                                {'\u2014'}
                                            </td>
                                            {/* Ship Date */}
                                            <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-500">
                                                {order.shipDate ? formatDate(order.shipDate) : '\u2014'}
                                            </td>
                                            {/* Created */}
                                            <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-500">
                                                {formatDate(order.createdAt)}
                                            </td>
                                            {/* Actions */}
                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                {isOperationsEntitled ? (
                                                    <select
                                                        value={order.status}
                                                        onChange={e => handleStatusChange(order.id, e.target.value)}
                                                        onClick={e => e.stopPropagation()}
                                                        className="bg-transparent border border-transparent hover:border-slate-200 rounded px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-200 cursor-pointer transition-colors"
                                                    >
                                                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                                            <option key={k} value={k}>{v.label}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs text-slate-400 font-medium px-2 py-1">
                                                        <Lock className="w-3 h-3" /> Read-only
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
              </div>
            </div>

            {/* Create Modal */}
            {isOperationsEntitled && showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
                        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-200">
                            <h2 className="text-lg font-semibold text-slate-900">New Sales Order</h2>
                            <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreate} className="space-y-4 px-6 py-5">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-slate-700">Order Number *</label>
                                <input
                                    type="text"
                                    required
                                    value={newOrder.orderNumber}
                                    onChange={e => setNewOrder(p => ({ ...p, orderNumber: e.target.value }))}
                                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="SO-001"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-slate-700">Customer Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={newOrder.customerName}
                                    onChange={e => setNewOrder(p => ({ ...p, customerName: e.target.value }))}
                                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="Acme Corp"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-slate-700">Items Count</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={newOrder.itemsCount}
                                        onChange={e => setNewOrder(p => ({ ...p, itemsCount: e.target.value }))}
                                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-slate-700">Ship Date</label>
                                    <input
                                        type="date"
                                        value={newOrder.shipDate}
                                        onChange={e => setNewOrder(p => ({ ...p, shipDate: e.target.value }))}
                                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-slate-700">Notes</label>
                                <textarea
                                    rows={2}
                                    value={newOrder.notes}
                                    onChange={e => setNewOrder(p => ({ ...p, notes: e.target.value }))}
                                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                                    placeholder="Optional notes..."
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 mt-2">
                                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                                >
                                    {creating ? 'Creating...' : 'Create Order'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
          </div>

            {/* Details Dialog */}
            <SalesOrderDetailsDialog
                orderId={selectedOrderId}
                isOpen={!!selectedOrderId}
                onClose={() => setSelectedOrderId(null)}
            />
        </div>
    );
}
